import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { angleToCardinalLabel } from './CompassWidget';
import BaseSlopeControls from '../Wizard/inputs/BaseSlopeControls';
import ConfirmDialog from '../ui/ConfirmDialog';

/**
 * SettingsDialog - Display garden settings with options to edit, reset, or create new garden
 *
 * Features:
 * - Shows current garden settings (size, soil, location)
 * - Four action buttons:
 *   1. "Einstellungen bearbeiten" → Re-open wizard with existing data
 *   2. "Setup-Assistenten zurücksetzen" → Clear wizard data, keep canvas objects
 *   3. "Neuen Garten erstellen" → Clear everything (wizard + canvas)
 *   4. "Abbrechen" → Close dialog
 * - Confirmation dialogs for destructive actions
 * - Keyboard support (Escape to close)
 *
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onEditSettings - Callback when "Edit Settings" is clicked
 * @param {function} onResetWizard - Callback when "Reset Wizard" is confirmed
 * @param {function} onNewGarden - Callback when "New Garden" is confirmed
 */
const SettingsDialog = ({
  isOpen,
  onClose,
  onEditSettings,
  onResetWizard,
  onNewGarden,
  onStartTour,
}) => {
  const { settings, getGardenArea, updateSettings } = useGardenSettings();

  // State for nested confirmation dialogs
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);
  const [showNewGardenConfirmDialog, setShowNewGardenConfirmDialog] =
    useState(false);

  // Grundgefälle nachträglich änderbar (#625): schreibt in
  // settings.heightDifferences.baseSlope, Auto-Save läuft über den bestehenden
  // updateSettings→isDirty→30s-Pfad. heightDifferences kann null sein (ebener
  // Garten ohne Zonen) — dann wird die Hülle beim ersten Setzen angelegt.
  const handleBaseSlopeChange = (nextBaseSlope) => {
    const current = settings.heightDifferences;
    updateSettings({
      heightDifferences: {
        enabled: current?.enabled ?? false,
        zones: current?.zones ?? [],
        ...current,
        baseSlope: nextBaseSlope,
      },
    });
  };

  // Keyboard support for main dialog
  useEffect(() => {
    const handleKeyDown = e => {
      if (
        e.key === 'Escape' &&
        isOpen &&
        !showResetConfirmDialog &&
        !showNewGardenConfirmDialog
      ) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showResetConfirmDialog, showNewGardenConfirmDialog]);

  // Handler for "Einstellungen bearbeiten" button
  const handleEditSettings = () => {
    onClose();
    onEditSettings();
  };

  // Handler for "Setup-Assistenten zurücksetzen" button
  const handleResetWizardClick = () => {
    setShowResetConfirmDialog(true);
  };

  // Handler for reset confirmation
  const handleConfirmReset = () => {
    setShowResetConfirmDialog(false);
    onClose();
    onResetWizard();
  };

  // Handler for reset cancellation
  const handleCancelReset = () => {
    setShowResetConfirmDialog(false);
    // Settings dialog remains open
  };

  // Handler for "Neuen Garten erstellen" button
  const handleNewGardenClick = () => {
    setShowNewGardenConfirmDialog(true);
  };

  // Handler for new garden confirmation
  const handleConfirmNewGarden = () => {
    setShowNewGardenConfirmDialog(false);
    onClose();
    onNewGarden();
  };

  // Handler for new garden cancellation
  const handleCancelNewGarden = () => {
    setShowNewGardenConfirmDialog(false);
    // Settings dialog remains open
  };

  // Handle backdrop click (only when no confirmation dialog is open)
  const handleBackdropClick = e => {
    if (
      e.target === e.currentTarget &&
      !showResetConfirmDialog &&
      !showNewGardenConfirmDialog
    ) {
      onClose();
    }
  };

  // Map soil type values to German labels
  const soilTypeLabels = {
    humus: 'Humusreich',
    loamy: 'Lehmig',
    loess: 'Lössboden',
    mixed: 'Mischboden',
    sandy: 'Sandig',
    clay: 'Tonig',
  };

  // Format settings values with fallbacks
  const gardenSize = (() => {
    if (settings.length && settings.width) {
      const area = settings.area || getGardenArea();
      return `${settings.length}m × ${settings.width}m (${area}m²)`;
    }
    // Fallback if outline hasn't been drawn yet
    return 'Umriss noch nicht gezeichnet';
  })();

  const soilTypeLabel = settings.soilType
    ? soilTypeLabels[settings.soilType] || settings.soilType
    : null;

  const soilInfo =
    soilTypeLabel && settings.phValue
      ? `${soilTypeLabel}, pH ${settings.phValue}`
      : 'Keine Angabe';

  // Map orientation values to German labels
  const orientationLabels = {
    north: 'Nord',
    northeast: 'Nordost',
    east: 'Ost',
    southeast: 'Südost',
    south: 'Süd',
    southwest: 'Südwest',
    west: 'West',
    northwest: 'Nordwest',
  };

  // Build location string from ZIP code and city
  const locationParts = [];
  if (settings.zipCode) locationParts.push(settings.zipCode);
  if (settings.city) locationParts.push(settings.city);
  const locationInfo =
    locationParts.length > 0 ? locationParts.join(', ') : 'Keine Angabe';

  // Build orientation string
  const orientationInfo = settings.orientation
    ? orientationLabels[settings.orientation] || settings.orientation
    : 'Keine Angabe';

  // Build north angle string
  const northAngleInfo = settings.northAngle != null
    ? `${settings.northAngle}° (${angleToCardinalLabel(settings.northAngle)})`
    : 'Nicht eingestellt';

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      {/* Main Settings Dialog */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- modal backdrop click-to-close */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-dialog-title"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        {/* Dialog Card */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 z-10">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2
              id="settings-dialog-title"
              className="text-lg font-semibold text-gray-900"
            >
              Garten-Einstellungen
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              aria-label="Dialog schließen"
            >
              <X size={20} />
            </button>
          </div>

          {/* Settings Summary */}
          <div className="p-6 pb-0 space-y-3">
            <SettingRow
              label="Name"
              value={settings?.name || 'Kein Name'}
            />
            <SettingRow label="Gartengröße" value={gardenSize} />
            <SettingRow label="Boden" value={soilInfo} />
            <SettingRow label="Standort" value={locationInfo} />
            <SettingRow label="Ausrichtung" value={orientationInfo} />
            <SettingRow label="Nordwinkel" value={northAngleInfo} />
          </div>

          {/* Grundgefälle — nachträglich änderbar (#625) */}
          <div className="px-6 pt-4">
            <BaseSlopeControls
              baseSlope={settings.heightDifferences?.baseSlope ?? null}
              onChange={handleBaseSlopeChange}
              northAngle={settings.northAngle}
              outline={settings.outline}
              idPrefix="settings-base-slope"
            />
          </div>

          {/* Action Buttons */}
          <div className="p-6 pt-0 space-y-3">
            <button
              onClick={handleEditSettings}
              className="w-full py-2.5 px-4 bg-emerald-500 text-white font-medium rounded hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
            >
              Einstellungen bearbeiten
            </button>
            <button
              onClick={handleResetWizardClick}
              className="w-full py-2.5 px-4 bg-emerald-500 text-white font-medium rounded hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
            >
              Setup-Assistenten zurücksetzen
            </button>
            <button
              onClick={handleNewGardenClick}
              className="w-full py-2.5 px-4 bg-emerald-500 text-white font-medium rounded hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
            >
              Neuen Garten erstellen
            </button>
            {onStartTour && (
              <button
                onClick={() => {
                  onClose();
                  onStartTour();
                }}
                className="w-full py-2.5 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
              >
                Kurze Tour erneut starten
              </button>
            )}
            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 bg-gray-200 text-gray-700 font-medium rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>

      {/* Reset Wizard Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetConfirmDialog}
        onConfirm={handleConfirmReset}
        onCancel={handleCancelReset}
        title="Setup-Assistent zurücksetzen?"
        message="Alle Einstellungen aus dem Setup-Assistenten werden gelöscht. Ihre gezeichneten Objekte bleiben erhalten. Fortfahren?"
        confirmLabel="Zurücksetzen"
        cancelLabel="Abbrechen"
        variant="danger"
      />

      {/* New Garden Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showNewGardenConfirmDialog}
        onConfirm={handleConfirmNewGarden}
        onCancel={handleCancelNewGarden}
        title="Neuen Garten erstellen?"
        message="Alle Einstellungen und gezeichneten Objekte werden gelöscht. Dies kann nicht rückgängig gemacht werden. Fortfahren?"
        confirmLabel="Neuen Garten erstellen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </>
  );
};

/**
 * SettingRow - Reusable component for displaying a setting label and value
 */
const SettingRow = ({ label, value }) => (
  <div className="flex items-baseline">
    <span className="font-medium text-gray-900 min-w-[120px]">{label}:</span>
    <span className="ml-2 text-gray-700">{value}</span>
  </div>
);

export default SettingsDialog;
